import React, { useCallback } from "react";
import { Formik } from "formik";
import { Row, Col } from "antd";
import { Form, Select, SubmitButton, Input } from "formik-antd";
import { useNavigate } from "react-router-dom";
import { integrationService, connectionService } from "/app/src/services";
import { useTranslation } from "react-i18next";
import { App, Integration } from "/app/src/models";
import { buildParams } from "/app/src/helpers/params";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { simpleSchemaBuilder } from "/app/src/helpers";
import Box from "/app/src/components/generic/components/box";

interface FormValues {
  name: string;
  connectionId?: number;
  baseTable: "Material" | "Order";
  type: "create" | "delete";
}

/**
 * Component for creating a new Data Pull integration
 * @param param0 app and setToggled
 */
export default function NewIntegration({
  app,
  setToggled,
}: {
  app: App;
  setToggled: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const baseTables = [
    { label: "Material", value: "Material" },
    { label: "Order", value: "Order" },
  ];
  const { data: connections } = useQuery({
    queryKey: [
      "connections",
      buildParams({ type: "[or]REST;GraphQL;MSSQL;IBM2" }),
    ],
    queryFn: () =>
      connectionService.getAll(
        buildParams({ type: "[or]REST;GraphQL;MSSQL;IBM2" }),
      ),
    initialData: { connections: [] },
    select: (data) => data.connections,
  });

  const { mutateAsync: createIntegration } = useMutation({
    mutationFn: (integration: Integration) =>
      integrationService.createSingle(integration),
    onSuccess: (response) => {
      queryClient.setQueryData(
        ["integration", response.integration.id],
        response.integration,
      );
    },
  });

  /**
   * Create the integration
   */
  const onSubmitHandler = useCallback(
    (values: FormValues) => {
      createIntegration({ ...values, appId: app.id }).then((response) => {
        navigate(`/apps/${app.id}/integrations/${response.integration.id}`);
        setToggled();
      });
    },
    [app, createIntegration, navigate, setToggled],
  );

  return (
    <Box>
      <div className="newIntegration">
        <Formik
          initialValues={{
            name: "",
            connectionId: undefined,
            baseTable: "Order",
            type: "create",
          }}
          onSubmit={onSubmitHandler}
          validationSchema={simpleSchemaBuilder([
            { name: "name", type: "string", required: true },
            { name: "connectionId", type: "number", required: true },
          ])}
        >
          {({ isSubmitting, isValid, values }) => (
            <Form layout="vertical">
              <Row justify="start" gutter={16}>
                <Col span={6}>
                  <Form.Item
                    name="name"
                    label={t("translation:integration_name")}
                  >
                    <Input
                      name="name"
                      placeholder={t("translation:enter_name")}
                      size="large"
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name="connectionId"
                    label={t("translation:connection")}
                  >
                    <Select
                      name="connectionId"
                      size="large"
                      placeholder={t("translation:select_connection")}
                    >
                      {connections.map((c) => (
                        <Select.Option value={c.id} key={c.id}>
                          {c.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item
                    name="baseTable"
                    label={t("translation:select_type")}
                  >
                    <Select
                      name="baseTable"
                      size="large"
                      placeholder={t("translation:select_type")}
                    >
                      {baseTables.map((t) => (
                        <Select.Option value={t.value} key={t.value}>
                          {t.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item name="type" label={t("translation:method")}>
                    <Select
                      name="type"
                      size="large"
                      placeholder={t("translation:select_type")}
                    >
                      {values.baseTable === "Order" && (
                        <Select.Option value={"delete"} key={"delete"}>
                          {"Deletion"}
                        </Select.Option>
                      )}

                      <Select.Option value={"create"} key={"create"}>
                        {"Creation"}
                      </Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <SubmitButton
                    type="primary"
                    size="large"
                    block
                    disabled={isSubmitting || !isValid}
                    style={{ marginTop: "30px" }}
                  >
                    {t("translation:create_integration")}
                  </SubmitButton>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </div>
    </Box>
  );
}
